import { createRouter, createWebHistory } from 'vue-router';
import AuthCallbackView from '@/views/auth/AuthCallbackView.vue';

// Routes
const routes = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: {
      requiresAuth: false,
      seo: {
        titleKey: 'landing.page_title',
        descriptionKey: 'landing.page_description',
      },
    },
  },
  {
    path: '/auth',
    component: () => import('@/views/auth/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: {
          requiresAuth: false,
          seo: {
            titleKey: 'auth.login-title',
            descriptionKey: 'auth.login-description',
          },
        },
      },
      {
        path: 'signup',
        name: 'signup',
        component: () => import('@/views/auth/SignupView.vue'),
        meta: {
          requiresAuth: false,
          seo: {
            titleKey: 'auth.signup-title',
            descriptionKey: 'auth.signup-description',
          },
        },
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: {
          requiresAuth: false,
          seo: {
            titleKey: 'auth.forgot-title',
            descriptionKey: 'auth.forgot-description',
          },
        },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: {
          requiresAuth: false,
          title: 'Reset Password',
          seo: {
            titleKey: 'auth.reset-title',
            descriptionKey: 'auth.reset-description',
            noindex: true,
          },
        },
      },
      {
        path: 'callback',
        name: 'auth-callback',
        component: AuthCallbackView,
        meta: {
          title: 'Authentication Callback',
          seo: { noindex: true },
        },
      },
    ],
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics',
    component: () => import('@/views/analytics/AnalyticsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'analytics',
        component: () => import('@/views/analytics/AnalyticsView.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/artists',
    component: () => import('@/views/artists/ArtistsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'artists',
        component: () => import('@/views/artists/ArtistListView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'new',
        name: 'create-artist',
        component: () => import('@/views/artists/ArtistEditView.vue'),
        meta: { requiresAuth: true, isNew: true },
      },
      {
        path: ':id/edit',
        name: 'edit-artist',
        component: () => import('@/views/artists/ArtistEditView.vue'),
        meta: { requiresAuth: true, isNew: false },
        props: true,
      },
    ],
  },
  {
    path: '/distributions',
    component: () => import('@/views/releases/ReleasesLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'releases',
        component: () => import('@/views/releases/ReleaseListView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'new',
        name: 'create-release',
        component: () => import('@/views/releases/ReleaseEditView.vue'),
        meta: { requiresAuth: true, isNew: true },
      },
      {
        path: 'new/:step',
        name: 'create-release-step',
        component: () => import('@/views/releases/ReleaseEditView.vue'),
        meta: { requiresAuth: true, isNew: true },
        props: true,
      },
      {
        path: ':id',
        name: 'view-release',
        component: () => import('@/views/releases/ReleaseDetailView.vue'),
        meta: { requiresAuth: true },
        props: true,
      },
      {
        path: ':id/whats-next',
        name: 'distribution-whats-next',
        component: () => import('@/views/releases/DistributionWhatsNextView.vue'),
        meta: { requiresAuth: true },
        props: true,
      },
      {
        path: ':id/edit',
        name: 'edit-release',
        component: () => import('@/views/releases/ReleaseEditView.vue'),
        meta: { requiresAuth: true, isNew: false },
        props: true,
      },
      {
        path: ':id/edit/:step',
        name: 'edit-release-step',
        component: () => import('@/views/releases/ReleaseEditView.vue'),
        meta: { requiresAuth: true, isNew: false },
        props: true,
      },
    ],
  },
  {
    path: '/playlists',
    component: () => import('@/views/playlists/PlaylistsLayout.vue'),
    children: [
      {
        path: '',
        name: 'playlists',
        component: () => import('@/views/playlists/PlaylistsView.vue'),
        meta: {
          requiresAuth: false,
          seo: {
            titleKey: 'playlists.seo-title',
            descriptionKey: 'playlists.seo-description',
          },
        },
      },
      {
        path: ':id/submit',
        name: 'playlist-submission',
        component: () => import('@/views/playlists/PlaylistSubmissionView.vue'),
        meta: { requiresAuth: true },
        props: true,
      },
    ],
    meta: { requiresAuth: false },
  },
  {
    path: '/royalties',
    name: 'royalties',
    component: () => import('@/views/royalties/RoyaltiesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/feed',
    name: 'feed',
    component: () => import('@/views/feed/FeedView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Artist Feed | Indiefy',
      description: 'Discover new releases from artists you follow and give feedback to earn credits.',
    },
  },
  {
    path: '/upgrade',
    name: 'upgrade',
    component: () => import('@/views/settings/UpgradeView.vue'),
    meta: {
      seo: {
        titleKey: 'upgrade.seo-title',
        descriptionKey: 'upgrade.seo-description',
      },
    },
  },
  {
    path: '/pro',
    name: 'pro',
    component: () => import('@/views/settings/UpgradeView.vue'),
    meta: {
      seo: {
        titleKey: 'upgrade.seo-title',
        descriptionKey: 'upgrade.seo-description',
      },
    },
  },
  {
    path: '/pro/yearly',
    name: 'pro-yearly',
    component: () => import('@/views/ProYearlyLandingView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Become PRO for $49/year - Indiefy',
      description: 'Upgrade to Indiefy PRO yearly. Keep 100% of your royalties, distribute to 150+ stores, get presaves, custom label and faster releases. $49/year.',
    },
  },
  {
    path: '/pro/welcome',
    name: 'pro-welcome',
    component: () => import('@/views/ProWelcomeView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Welcome to PRO - Indiefy',
      description: 'Activating your Indiefy PRO subscription.',
    },
  },
  {
    path: '/distribute',
    name: 'distribute-landing',
    component: () => import('@/views/DistributeLandingView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Distribute your music to 150+ stores - Indiefy',
      description: 'Upload your track, fill in a few details, and reach Spotify, Apple Music and 150+ stores worldwide. No credit card required.',
    },
  },
  {
    path: '/distribute/success',
    name: 'distribute-success',
    component: () => import('@/views/DistributeSuccessView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Your release is in! - Indiefy',
      description: 'Your release has been created. Review and submit it to send your music to 150+ stores worldwide.',
    },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/account/AccountView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Account Settings',
      description: 'Manage your account settings, subscription, and preferences.',
    },
  },
  {
    path: '/account/subscription',
    name: 'account-subscription',
    component: () => import('@/views/account/AccountView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Subscription Settings',
      description: 'Manage your subscription and billing preferences.',
    },
    props: { tab: 'subscription' },
  },
  {
    path: '/account/notifications',
    name: 'account-notifications',
    component: () => import('@/views/account/AccountView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Notification Settings',
      description: 'Manage your notification preferences.',
    },
    props: { tab: 'notifications' },
  },
  {
    path: '/account/payout',
    name: 'account-payout',
    component: () => import('@/views/account/AccountView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Payout Settings',
      description: 'Configure your payment methods for royalty payouts.',
    },
    props: { tab: 'payout' },
  },
  {
    path: '/account/blocked',
    name: 'account-blocked',
    component: () => import('@/views/account/BlockedAccountView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Account Restricted',
      description: 'Account temporarily restricted - Contact support for assistance',
      noHeader: true,
    },
  },
  {
    path: '/tools',
    name: 'tools',
    component: () => import('@/views/ToolsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Tools',
    },
  },
  {
    path: '/tools/mastering',
    name: 'tool-mastering',
    component: () => import('@/views/MasteringToolView.vue'),
  },
  {
    path: '/tools/mastering/:id',
    name: 'tool-mastering-edit',
    component: () => import('@/views/MasteringEditView.vue'),
    props: true,
  },
  {
    path: '/tools/presave',
    name: 'tool-presave',
    component: () => import('@/views/PresaveToolView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/presave/:id',
    name: 'tool-presave-detail',
    component: () => import('@/views/PresaveCampaignDetailView.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/tools/smartlinks',
    name: 'tool-smartlinks',
    component: () => import('@/views/SmartLinksToolView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/smartlinks/:id',
    name: 'tool-smartlinks-edit',
    component: () => import('@/views/SmartLinksEditView.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/tools/indiefy-me',
    name: 'tool-indiefy-me',
    component: () => import('@/views/IndiefyMeToolView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/indiefy-me/:id',
    name: 'tool-indiefy-me-edit',
    component: () => import('@/views/IndiefyMeEditView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Indiefy Me',
    },
    props: true,
  },
  {
    path: '/tools/lyrics',
    name: 'tool-lyrics',
    component: () => import('@/views/LyricGeneratorToolView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/lyrics/:id',
    name: 'tool-lyrics-new',
    component: () => import('@/views/LyricGeneratorFormView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/banner-generator',
    name: 'tool-banner-generator',
    component: () => import('@/views/BannerGeneratorToolView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/songtools',
    name: 'tool-songtools',
    component: () => import('@/views/SongToolsWidgetView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tools/metadata-change',
    name: 'tool-metadata-change',
    component: () => import('@/views/MetadataChangeRequestView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Metadata Change Request',
      description: 'Request metadata changes for an in-process or published release.',
    },
  },
  {
    path: '/referral-program',
    name: 'referral-program',
    component: () => import('@/views/ReferralProgramView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Referral Program',
    },
  },
  {
    path: '/forms/:id',
    name: 'form-dynamic',
    component: () => import('@/views/FormDynamicView.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/form-submissions',
    name: 'form-submissions',
    component: () => import('@/views/FormSubmissionsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Form Submissions',
      description: 'View the status of all your submitted forms.',
    },
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('@/views/BlogListView.vue'),
    meta: {
      requiresAuth: false,
      seo: {
        titleKey: 'blog.page_title',
        descriptionKey: 'blog.description',
      },
    },
  },
  {
    path: '/blog/:id',
    name: 'blog-post',
    component: () => import('@/views/BlogPostView.vue'),
    meta: { requiresAuth: false },
    props: true,
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: () => import('@/views/TermsAndConditionsView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Terms and Conditions | Indiefy',
      metaDescription: 'Read Indiefy\'s terms and conditions for music distribution services. Understand your rights and responsibilities as an independent artist using our platform.',
      seo: {
        titleKey: 'terms.seo-title',
        descriptionKey: 'terms.seo-description',
      },
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/PrivacyPolicyView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Privacy Policy | Indiefy',
      metaDescription: 'Learn how Indiefy protects your privacy and handles your personal information. Read our comprehensive privacy policy for music distribution services.',
      seo: {
        titleKey: 'privacy.seo-title',
        descriptionKey: 'privacy.seo-description',
      },
    },
  },
  {
    path: '/cookie-policy',
    name: 'cookie-policy',
    component: () => import('@/views/CookiePolicyView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Cookie Policy | Indiefy',
      metaDescription: 'Learn about how Indiefy uses cookies and similar technologies. Manage your cookie preferences and understand your choices.',
      seo: {
        titleKey: 'cookie.seo-title',
        descriptionKey: 'cookie.seo-description',
      },
    },
  },
  {
    path: '/music-distribution-and-monetization',
    name: 'music-monetization-landing',
    component: () => import('@/views/MusicMonetizationLandingView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Music Distribution & Monetization | Indiefy',
      metaDescription: 'Learn how to monetize your music globally with Indiefy. Distribute your tracks to 150+ platforms, maximize your royalties, and join the best music monetization platform for independent artists.',
      seo: {
        titleKey: 'music-monetization.seo-title',
        descriptionKey: 'music-monetization.seo-description',
      },
    },
  },
  {
    path: '/free-music-distribution',
    name: 'FreeMusicDistribution',
    component: () => import('@/views/FreeMusicDistributionView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Free Music Distribution for Independent Artists | Indiefy',
      description: 'Start your music career with Indiefy\'s free music distribution. Get your songs on Spotify, Apple Music, and more streaming platforms at no cost.',
      seo: {
        titleKey: 'free-distribution.seo-title',
        descriptionKey: 'free-distribution.seo-description',
      },
    },
  },
  {
    path: '/distribution-glossary',
    name: 'distribution-glossary',
    component: () => import('@/views/DistributionGlossaryView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Music Distribution Glossary | Indiefy',
      description: 'Demystify music distribution with our comprehensive glossary. Learn the meaning of DSP, ISRC, UPC, royalties, mastering and 40+ essential industry terms for independent artists.',
      seo: {
        titleKey: 'glossary.seo-title',
        descriptionKey: 'glossary.seo-description',
      },
    },
  },
  {
    path: '/artists-catalog',
    name: 'ArtistsCatalog',
    component: () => import('@/views/ArtistsCatalogView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Discover Independent Artists | Indiefy Catalog',
      description: 'Explore thousands of talented independent artists from around the world. Discover new music across all genres and support emerging artists on their musical journey.',
      seo: {
        titleKey: 'artists-catalog.seo-title',
        descriptionKey: 'artists-catalog.seo-description',
      },
    },
  },
  {
    path: '/release/:key',
    name: 'presave-public',
    component: () => import('@/views/PublicPresaveView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Pre-save Campaign',
      noHeader: true,
      description: 'Pre-save this upcoming release on Spotify and be the first to hear it when it drops.',
    },
    props: true,
  },
  {
    path: '/spotify-auth',
    name: 'spotify-auth',
    component: () => import('@/views/SpotifyAuthView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Spotify Authentication',
      noHeader: true,
      description: 'Connecting to Spotify...',
      seo: { noindex: true },
    },
  },
  {
    path: '/promotion',
    name: 'promotion',
    component: () => import('@/views/IndiefyPromotionView.vue'),
    meta: {
      requiresAuth: false,
      noHeader: true,
      title: 'Music Promotion Services | Indiefy Promotion',
      description: 'Promote your music with playlist placements and digital ads. Starting at just $10/day, get your tracks discovered by new fans through our professional promotion services.',
      seo: {
        titleKey: 'promotion.seo-title',
        descriptionKey: 'promotion.seo-description',
      },
    },
  },
  {
    path: '/cover-editor',
    name: 'cover-editor',
    component: () => import('@/views/IndiefyCoverEditorView.vue'),
    meta: {
      requiresAuth: false,
      noHeader: true,
      title: 'Free Album Cover Maker - Indiefy Cover Editor',
      description: 'Create stunning album covers for your music with our free cover editor. Choose from professional templates and customize them instantly.',
      seo: {
        titleKey: 'cover-editor.seo-title',
        descriptionKey: 'cover-editor.seo-description',
      },
    },
  },
  {
    path: '/mastering',
    name: 'ai-mastering',
    component: () => import('@/views/AIMasteringView.vue'),
    meta: {
      requiresAuth: false,
      noHeader: true,
      title: 'AI Music Mastering Service - Professional Audio Mastering - Indiefy',
      description: 'Master your tracks with AI-powered technology. Upload, preview presets, and download professional mastered audio for just $15 per track.',
      seo: {
        titleKey: 'ai-mastering.seo-title',
        descriptionKey: 'ai-mastering.seo-description',
      },
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      requiresAuth: false,
      seo: { noindex: true },
    },
  },
];

// Create router
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If user is using browser back/forward, restore saved position
    if (savedPosition) {
      return savedPosition;
    } else {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  },
});

// Router guards
router.beforeEach(async (to, from, next) => {
  // Import auth store dynamically to avoid circular dependency
  const { useAuthStore } = await import('@/store/auth');
  const authStore = useAuthStore();

  // console.log('🛣️ Router guard:', {
  //   to: to.name,
  //   isAuthenticated: authStore.isAuthenticated,
  //   user: authStore.user,
  //   isBlocked: authStore.isAuthenticated ? authStore.isUserBlocked() : 'N/A',
  // });

  // Magic-link arrival: the backend just set our session cookie and
  // redirected here with `?magic_link=1`, but `localStorage['indiefy-user']`
  // hasn't been populated yet. `initializeAuth()` in main.js kicks off a
  // `verifySession()` for this case, but it races this guard's first run.
  // Awaiting here guarantees `isAuthenticated` reflects the freshly
  // hydrated session before the `requiresAuth` check below decides whether
  // to bounce to /auth/login.
  if (to.query.magic_link === '1' && !authStore.isAuthenticated) {
    await authStore.verifySession();
  }

  // Check if user is blocked (except when already going to blocked page)
  if (to.name !== 'account-blocked' && authStore.isAuthenticated && authStore.isUserBlocked()) {
    console.log('🚫 User is blocked, redirecting to blocked page');
    return next('/account/blocked');
  }

  // Handle routes that require authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      // No valid session, redirect to login
      return next({
        name: 'login',
        query: { redirect: to.fullPath },
      });
    }
  }

  // Redirect authenticated users away from auth pages
  if ((to.name === 'login' || to.name === 'signup') && authStore.isAuthenticated) {
    // Don't redirect if user is blocked
    if (authStore.isUserBlocked()) {
      return next('/account/blocked');
    }

    if (to.query.redirect) {
      if (to.query.redirect.startsWith('https://')) {
        window.location.href = to.query.redirect;
        return;
      } else {
        return next(to.query.redirect);
      }
    }

    return next('/dashboard');
  }

  next();
});

export default router;